import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';
import styles from './Project.module.css';
import { useInView } from 'react-intersection-observer';

interface ProjectItem {
  id: number;
  title: string;
  image: string;
  image_url: string;
  created_at?: string;
  updated_at?: string;
}

const Project: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // triggerOnce: false -> animation replays every time you scroll it into view
  const { ref: headerRef, inView: headerInView } = useInView({ threshold: 0.2, triggerOnce: false });
  const { ref: cardsRef, inView: cardsInView } = useInView({ threshold: 0.1, triggerOnce: false });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        console.log('Fetching projects from API...');
        const response = await fetch(apiUrl('/api/projects'));

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        console.log('API Response:', result);

        if (result.success) {
          setProjects(result.data);
        } else {
          setError('API returned unsuccessful response');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to fetch projects: ${errorMessage}`);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSlidesToShow(1);
        setIsMobile(true);
      } else if (window.innerWidth <= 1024) {
        setSlidesToShow(2);
        setIsMobile(false);
      } else {
        setSlidesToShow(3);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset position whenever the visible card count changes (resize), so the
  // track never ends up translated past its new valid range.
  useEffect(() => {
    setCurrentSlide(0);
  }, [slidesToShow]);

  // totalSlides = number of valid track positions. With a continuous track,
  // each "slide" moves the window by exactly one card.
  const maxIndex = Math.max(projects.length - slidesToShow, 0);
  const totalSlides = projects.length > 0 ? maxIndex + 1 : 0;

  const nextSlide = () => {
    if (totalSlides === 0) return;
    setCurrentSlide((prev) =>
      prev === totalSlides - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    if (totalSlides === 0) return;
    setCurrentSlide((prev) =>
      prev === 0 ? totalSlides - 1 : prev - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Auto-advance carousel every 4 seconds — moves ONE card at a time
  useEffect(() => {
    if (totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const handleProjectClick = (project: ProjectItem) => {
    navigate(`/project/${project.id}`, {
      state: {
        projectData: project
      }
    });
  };

  if (loading) {
    return (
      <section className={styles.projectSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Completed Projects</h2>
          </div>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading projects from database...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.projectSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Completed Projects</h2>
          </div>
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>⚠️ {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (projects.length === 0) {
    return (
      <section className={styles.projectSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Completed Projects</h2>
          </div>
          <div className={styles.emptyState}>
            <p>No projects available. Check back soon!</p>
          </div>
        </div>
      </section>
    );
  }

  // Percentage width of one card, used to shift the track by exactly one card
  const cardStepPercent = 100 / slidesToShow;

  return (
    <section className={styles.projectSection}>
      <div className={styles.container}>
        <div
          ref={headerRef}
          className={`${styles.sectionHeader} ${headerInView ? styles.animateHeader : ''}`}
        >
          <h2 className={styles.scrollTitle}>
            Completed Projects
            <span className={styles.titleUnderline}></span>
          </h2>
        </div>

        <div ref={cardsRef} className={styles.carouselContainer}>
          <button className={styles.carouselBtn} onClick={prevSlide}>
            <i className="fas fa-chevron-left"></i>
          </button>

          <div className={styles.carouselTrack}>
            {/* Single continuous track — advancing shifts by ONE card, not a full group */}
            <div
              className={styles.projectsGrid}
              style={{
                transform: isMobile
                  ? `translateY(calc(-${currentSlide * 100}%))`
                  : `translateX(calc(-${currentSlide * cardStepPercent}%))`
              }}
            >
              {projects.map((project, idx) => (
                <div
                  key={project.id}
                  className={`${styles.col} ${cardsInView ? styles.animateCard : ''}`}
                  style={{ transitionDelay: cardsInView ? `${Math.min(idx, 5) * 60}ms` : '0ms' }}
                >
                  <div className={styles.projectCard}>
                    <div className={styles.projectImage}>
                      <img
                        src={project.image_url}
                        alt={project.title}
                        decoding="async"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          const filename = project.image?.split('/').pop() || '';

                          const fallbacks = [
                            apiUrl(`/images/projects/${filename}`),
                            apiUrl(`/storage/projects/${filename}`),
                          ];

                          let fallbackIndex = 0;
                          const tryFallback = () => {
                            if (fallbackIndex < fallbacks.length) {
                              img.src = fallbacks[fallbackIndex];
                              fallbackIndex++;
                            } else {
                              const firstLetter = project.title?.charAt(0) || 'P';
                              img.src = `https://placehold.co/600x400/2563eb/white?text=${encodeURIComponent(firstLetter)}`;
                            }
                          };

                          img.onerror = tryFallback;
                          tryFallback();
                        }}
                      />
                    </div>

                    <div className={styles.projectContent}>
                      <div className={styles.projectTitle}>
                        <h5>{project.title}</h5>
                      </div>

                      <div className={styles.projectCta}>
                        <button
                          className={styles.readMoreBtn}
                          onClick={() => handleProjectClick(project)}
                        >
                          Read More
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button className={styles.carouselBtn} onClick={nextSlide}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {totalSlides > 1 && (
          <div className={styles.carouselDots}>
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${currentSlide === index ? styles.activeDot : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        )}

        <div className={styles.slideIndicator}>
          <span>Slide {currentSlide + 1} of {totalSlides}</span>
        </div>
      </div>
    </section>
  );
};

export default Project;